import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Divider, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Link, RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { PasswordRequirementsButton } from '@/components/PasswordRequirementsButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  keyicon: {
    width: 32,
    height: 32,
  },
  image16x16: {
    width: 16,
    height: 16,
  },
});

export default function ChangePassword() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const passwordInput = useRef<RNTextInput>(null);
  const newPasswordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [currentPasswordShown, setCurrentPasswordShown] = useState(false);
  const [newPasswordShown, setNewPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      password: '',
      newpassword: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchAuthorized(
        "ChangePassword.onSubmit",
        "/rabbl/api/changepassword",
        "POST",
        {
          password: data.password,
          newpassword: data.newpassword,
        }
      );

      setError('Password successfully changed.');
      AppUtils.showInformationalToast("Password successfully changed.");

    } catch (e : any) {
      AppUtilities.handleException(e, "ChangePassword.onSubmit");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
    } finally {
      hideSpinner();
    }
  }, [communityMembership]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (passwordInput && passwordInput.current) {
        passwordInput.current?.focus();
      }
    }
  }, [passwordInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Change password"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Enter your old password and a new password:</AppText>

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.flexRowGap5}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Old Password"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={() => newPasswordInput.current?.focus()}
                  secureTextEntry={currentPasswordShown ? false : true}
                />
                <PasswordShowHideButton passwordShown={currentPasswordShown} setPasswordShown={setCurrentPasswordShown} largeKey={true} />
                <View style={{ width: 19, height: 16}}></View>
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Old password is required. ^</AppText>}

          <Controller
            control={control}
            name="newpassword"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={newPasswordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="New Password"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  secureTextEntry={newPasswordShown ? false : true}
                />
                <PasswordRequirementsButton />
                <PasswordShowHideButton passwordShown={newPasswordShown} setPasswordShown={setNewPasswordShown} />
              </View>
            )}
          />
          {errors.newpassword && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ New password is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Change password</Button95>
          </View>

          { (error && error.length > 0) ?
                (
                  <>
                    <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>{error}</AppText>
                  </>
                )
              :
                null
          }
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
